import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';

/**
 * Panel d'affichage des résultats de scan OWASP ZAP
 * Intégré dans la section Analytics/Sécurité
 */
const ZapScanPanel = ({ token }) => {
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les scans récents
  const loadScans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/zap-scans?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setScans(data.data);
        // Sélectionner automatiquement le dernier scan
        if (data.data.length > 0 && !selectedScan) {
          loadScanDetail(data.data[0].id);
        }
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  }, [token, selectedScan]);

  // Charger les statistiques
  const loadStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/zap-scans/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  }, [token]);

  // Charger le détail d'un scan
  const loadScanDetail = async (scanId) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/zap-scans/${scanId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedScan(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement détail scan:', err);
    }
  };

  useEffect(() => {
    loadScans();
    loadStats();
  }, [loadScans, loadStats]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#28a745';
    if (score >= 70) return '#ffc107';
    if (score >= 50) return '#fd7e14';
    return '#dc3545';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Bon';
    if (score >= 50) return 'Moyen';
    return 'Critique';
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Chargement...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>🔐</span>
            Scans OWASP ZAP
          </h3>
          <p style={{ margin: '5px 0 0', color: '#666', fontSize: '14px' }}>
            Analyse automatique des vulnérabilités web (hebdomadaire)
          </p>
        </div>
        {stats && stats.last_scan_date && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>Dernier scan</div>
            <div style={{ fontWeight: '600' }}>{formatDate(stats.last_scan_date)}</div>
          </div>
        )}
      </div>

      {error && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Statistiques globales */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '25px' }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: getScoreColor(Math.round(stats.avg_score || 0)) }}>
              {Math.round(stats.avg_score || 0)}
            </div>
            <div style={{ color: '#666', fontSize: '14px' }}>Score moyen</div>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#333' }}>
              {stats.total_scans || 0}
            </div>
            <div style={{ color: '#666', fontSize: '14px' }}>Scans effectués</div>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#dc3545' }}>
              {stats.total_fails || 0}
            </div>
            <div style={{ color: '#666', fontSize: '14px' }}>Vulnérabilités</div>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#ffc107' }}>
              {stats.total_warns || 0}
            </div>
            <div style={{ color: '#666', fontSize: '14px' }}>Avertissements</div>
          </div>
        </div>
      )}

      {/* Évolution du score */}
      {stats?.evolution && stats.evolution.length > 1 && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e0e0e0', marginBottom: '25px' }}>
          <h4 style={{ margin: '0 0 15px', fontSize: '16px' }}>Évolution du score de sécurité</h4>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '100px' }}>
            {stats.evolution.map((scan, idx) => (
              <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  style={{
                    width: '100%',
                    height: `${scan.security_score}%`,
                    background: `linear-gradient(to top, ${getScoreColor(scan.security_score)}, ${getScoreColor(scan.security_score)}aa)`,
                    borderRadius: '4px 4px 0 0',
                    minHeight: '10px',
                    transition: 'height 0.3s'
                  }}
                  title={`Score: ${scan.security_score}`}
                />
                <div style={{ fontSize: '10px', color: '#999', marginTop: '5px' }}>
                  {new Date(scan.scan_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {scans.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#666', background: '#f8f9fa', borderRadius: '12px' }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>🔍</div>
          <p style={{ margin: 0, fontSize: '16px' }}>Aucun scan disponible</p>
          <p style={{ margin: '10px 0 0', fontSize: '14px' }}>
            Le premier scan sera effectué automatiquement chaque semaine.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: selectedScan ? '1fr 2fr' : '1fr', gap: '20px' }}>
          {/* Liste des scans */}
          <div>
            <h4 style={{ margin: '0 0 15px' }}>Historique ({scans.length})</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {scans.map(scan => {
                const isSelected = selectedScan?.id === scan.id;
                const scoreColor = getScoreColor(scan.security_score);

                return (
                  <div
                    key={scan.id}
                    onClick={() => loadScanDetail(scan.id)}
                    style={{
                      padding: '15px',
                      background: isSelected ? '#e3f2fd' : 'white',
                      border: isSelected ? '2px solid #2196f3' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600' }}>{formatDate(scan.scan_date)}</span>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '700',
                        background: `${scoreColor}20`,
                        color: scoreColor
                      }}>
                        {scan.security_score}/100
                      </span>
                    </div>
                    <div style={{ marginTop: '10px', display: 'flex', gap: '15px', fontSize: '13px' }}>
                      <span style={{ color: '#28a745' }}>✓ {scan.pass_count} PASS</span>
                      <span style={{ color: '#ffc107' }}>⚠ {scan.warn_count} WARN</span>
                      <span style={{ color: '#dc3545' }}>✗ {scan.fail_count} FAIL</span>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                      {scan.triggered_by === 'cron' ? '🤖 Automatique' : '👤 Manuel'} • {scan.scan_type}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Détail du scan sélectionné */}
          {selectedScan && (
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e0e0e0' }}>
              {/* Header du détail */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
                <div>
                  <h4 style={{ margin: 0 }}>Scan du {formatDate(selectedScan.scan_date)}</h4>
                  <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#999' }}>
                    Cible: {selectedScan.target_url} • Durée: {selectedScan.duration_seconds}s
                  </p>
                </div>
                <div style={{
                  padding: '10px 20px',
                  borderRadius: '12px',
                  textAlign: 'center',
                  background: `${getScoreColor(selectedScan.security_score)}15`,
                  border: `2px solid ${getScoreColor(selectedScan.security_score)}`
                }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: getScoreColor(selectedScan.security_score) }}>
                    {selectedScan.security_score}
                  </div>
                  <div style={{ fontSize: '12px', color: getScoreColor(selectedScan.security_score) }}>
                    {getScoreLabel(selectedScan.security_score)}
                  </div>
                </div>
              </div>

              {/* Résumé */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
                <div style={{ padding: '15px', background: '#d4edda', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#28a745' }}>{selectedScan.pass_count}</div>
                  <div style={{ fontSize: '12px', color: '#155724' }}>PASS</div>
                </div>
                <div style={{ padding: '15px', background: '#fff3cd', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#856404' }}>{selectedScan.warn_count}</div>
                  <div style={{ fontSize: '12px', color: '#856404' }}>WARN</div>
                </div>
                <div style={{ padding: '15px', background: '#f8d7da', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#721c24' }}>{selectedScan.fail_count}</div>
                  <div style={{ fontSize: '12px', color: '#721c24' }}>FAIL</div>
                </div>
                <div style={{ padding: '15px', background: '#e2e3e5', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#383d41' }}>{selectedScan.info_count}</div>
                  <div style={{ fontSize: '12px', color: '#383d41' }}>INFO</div>
                </div>
              </div>

              {/* Alertes détaillées */}
              {selectedScan.alerts && selectedScan.alerts.length > 0 && (
                <div>
                  <h5 style={{ margin: '0 0 15px', color: '#333' }}>
                    Alertes détectées ({selectedScan.alerts.length})
                  </h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '400px', overflowY: 'auto' }}>
                    {selectedScan.alerts.map((alert, idx) => (
                      <div key={idx} style={{
                        padding: '15px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${alert.type === 'FAIL' ? '#dc3545' : '#ffc107'}`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontWeight: '600' }}>{alert.name}</span>
                          <span style={{
                            padding: '3px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: alert.type === 'FAIL' ? '#dc3545' : '#ffc107',
                            color: alert.type === 'FAIL' ? 'white' : '#333'
                          }}>
                            {alert.type}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                          Rule ID: {alert.ruleId} • {alert.count} occurrence(s)
                        </div>
                        {alert.urls && alert.urls.length > 0 && (
                          <div style={{ fontSize: '12px', background: '#fff', padding: '8px', borderRadius: '4px' }}>
                            <strong>URLs affectées:</strong>
                            <div style={{ fontFamily: 'monospace', marginTop: '5px', maxHeight: '80px', overflowY: 'auto' }}>
                              {alert.urls.map((url, i) => (
                                <div key={i} style={{ marginBottom: '3px', wordBreak: 'break-all' }}>
                                  {url.url} ({url.statusCode})
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Message si aucune alerte */}
              {(!selectedScan.alerts || selectedScan.alerts.length === 0) && (
                <div style={{ padding: '30px', background: '#d4edda', borderRadius: '8px', textAlign: 'center', color: '#155724' }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>✅</div>
                  <p style={{ margin: 0, fontWeight: '600' }}>Aucune vulnérabilité détectée</p>
                  <p style={{ margin: '5px 0 0', fontSize: '14px' }}>Tous les tests de sécurité ont réussi.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ZapScanPanel;
